import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Button } from '@workspace/ui/components/Button'
import { PasswordInput } from '@workspace/ui/components/Textfield'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@workspace/ui/components/Form'
import { Input } from '@workspace/ui/components/Textfield'
import { BsSelect } from '@workspace/ui/components/Select'
import { AuthLayout } from '@workspace/ui/components/AuthLayout'
import { AuthExpiredState } from '@workspace/ui/components/AuthExpiredState'
import { AuthPendingApprovalState } from '@workspace/ui/components/AuthPendingApprovalState'
import { api } from '@/shared/lib/api'
import { toast } from '@workspace/ui/components/Sonner'
import { SetPasswordRequest } from '@workspace/lib/api'
import { login as loginToStore } from '@/shared/store/auth.store'
import { initializeTokenService } from '@/shared/lib/tokenService'

export const Route = createFileRoute('/users/invite')({
    component: InvitePage,
    validateSearch: (search: Record<string, unknown>) => {
        return {
            token: (search.token as string) || '',
        }
    },
})

const createSetPasswordSchema = (t: (key: string) => string, isEditable: boolean = false) =>
    z
        .object({
            fullName: isEditable
                ? z.string().min(1, t('setPassword.validation.fullNameRequired'))
                : z.string().optional(),
            position: z.string().optional(),
            regionId: isEditable
                ? z.string().min(1, t('setPassword.validation.regionRequired'))
                : z.string().optional(),
            areaIds: z.array(z.string()).optional(),
            password: z
                .string()
                .min(1, t('setPassword.validation.passwordRequired'))
                .min(8, t('setPassword.validation.passwordMin'))
                .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/, {
                    message: t('setPassword.validation.passwordPattern'),
                }),
            confirmPassword: z.string().min(1, t('setPassword.validation.confirmPasswordRequired')),
        })
        .refine(data => data.password === data.confirmPassword, {
            message: t('setPassword.validation.passwordMismatch'),
            path: ['confirmPassword'],
        })

type SetPasswordFormData = z.infer<ReturnType<typeof createSetPasswordSchema>>

function InvitePage() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const search = useSearch({ from: Route.fullPath }) as { token: string }
    const [isLoading, setIsLoading] = useState(false)
    const [isBlocked, setIsBlocked] = useState(false)

    // Fetch invitation info from token
    const {
        data: invitationInfo,
        isLoading: isLoadingInfo,
        error: invitationError,
    } = useQuery({
        queryKey: ['invitation-info', search.token],
        queryFn: () => api.auth.getUserByToken(search.token),
        enabled: !!search.token,
        retry: false,
        refetchOnWindowFocus: false,
    })

    // Allow form to be editable
    const isFormEditable = true
    const setPasswordSchema = useMemo(() => createSetPasswordSchema(t, isFormEditable), [t, isFormEditable])

    const form = useForm<SetPasswordFormData>({
        resolver: zodResolver(setPasswordSchema),
        defaultValues: {
            fullName: '',
            position: '',
            regionId: '',
            areaIds: [],
            password: '',
            confirmPassword: '',
        },
    })

    // Update resolver when schema changes
    useEffect(() => {
        form.clearErrors()
    }, [setPasswordSchema, form])

    // Pre-fill form when invitationInfo is loaded
    useEffect(() => {
        if (invitationInfo) {
            form.setValue('fullName', invitationInfo.fullName)
            form.setValue('position', invitationInfo.position)
            if (invitationInfo.region?.id) {
                form.setValue('regionId', invitationInfo.region.id)
            }
            if (invitationInfo.areas && invitationInfo.areas.length > 0) {
                form.setValue(
                    'areaIds',
                    invitationInfo.areas.map(area => area.id),
                )
            }
        }
    }, [invitationInfo, form])

    // Fetch regions and areas for dropdown - always fetch to populate select options
    const { data: regions } = useQuery({
        queryKey: ['regions'],
        queryFn: () => api.region.list({ page: 1, take: 100 }),
    })

    const watchedRegionId = form.watch('regionId')

    const { data: areas } = useQuery({
        queryKey: ['areas', watchedRegionId],
        queryFn: () => api.area.list({ regionIds: watchedRegionId || undefined, page: 1, take: 100 }),
        enabled: !!watchedRegionId,
    })

    const onSubmit = async (data: SetPasswordFormData) => {
        if (!search.token || search.token.trim() === '') {
            toast.error({
                title: t('setPassword.error.tokenRequired'),
            })
            return
        }

        setIsLoading(true)

        try {
            const payload: SetPasswordRequest = {
                token: search.token.trim(),
                password: data.password.trim(),
                fullName: data.fullName?.trim(),
                position: data.position?.trim(),
                regionId: data.regionId,
                areaIds: data.areaIds || [],
            }
            const response = await api.auth.setPassword(payload)

            // If backend returns login response (user + token), auto-login and redirect to dashboard
            if (response && 'user' in response && 'token' in response && 'refreshToken' in response) {
                loginToStore(
                    response.user,
                    response.token.accessToken,
                    response.refreshToken.refreshToken,
                    response.token.expiresIn,
                )

                // Initialize token service to start proactive refresh
                initializeTokenService()

                toast.success({
                    title: t('setPassword.success.title'),
                    description: t('setPassword.success.autoLogin'),
                })
                navigate({ to: '/dashboard' })
            } else {
                // Otherwise, just show success and redirect to login
                toast.success({
                    title: t('setPassword.success.title'),
                    description: t('setPassword.success.description'),
                })
                navigate({ to: '/login' })
            }
        } catch (error: any) {
            const status = error?.response?.status
            const message = error?.response?.data?.message || error?.message || ''
            const errorCode = error?.response?.data?.code || ''

            // Check if user is blocked
            const isUserBlocked =
                message === 'error.userBlocked' ||
                message.includes('userBlocked') ||
                message.includes('blocked') ||
                errorCode === 'USER_BLOCKED'

            if (isUserBlocked) {
                setIsBlocked(true)
                return
            }

            // Check if token is expired
            const isExpired =
                status === 400 ||
                status === 404 ||
                message.includes('expired') ||
                message.includes('hết hạn') ||
                message.includes('invalid') ||
                message.includes('không hợp lệ') ||
                errorCode === 'INVITATION_EXPIRED' ||
                errorCode === 'EXPIRED'

            if (isExpired) {
                // Show friendly message for expired invitation
                toast.error({
                    title: t('setPassword.expired.title', 'Link mời đã hết hạn'),
                    description: t(
                        'setPassword.expired.submitMessage',
                        'Rất tiếc, link mời tham gia hệ thống của bạn đã hết hạn. Vui lòng liên hệ với quản trị viên để được gửi lại link mời mới.',
                    ),
                })
            } else {
                // Show other errors normally
                const errorMessage =
                    error.response?.data?.message ||
                    error.response?.data?.error ||
                    error.response?.data?.errors?.password?.[0] ||
                    error.response?.data?.errors?.token?.[0] ||
                    (typeof error.response?.data === 'string' ? error.response.data : null) ||
                    error.message ||
                    t('setPassword.error.title')

                toast.error({
                    title: errorMessage,
                })
            }
        } finally {
            setIsLoading(false)
        }
    }

    const regionOptions = useMemo(() => {
        return regions?.data.map(region => ({ name: region.name, id: region.id })) || []
    }, [regions])

    const areaOptions = useMemo(() => {
        return areas?.data.map(area => ({ name: area.name, id: area.id })) || []
    }, [areas])

    if (isLoadingInfo) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <div className="text-center">
                    <p className="text-muted-foreground">{t('setPassword.loading')}</p>
                </div>
            </div>
        )
    }

    // Show blocked user message
    if (isBlocked) {
        return (
            <AuthLayout maxWidth="sm">
                <AuthPendingApprovalState
                    title={t('login.noPermissionTitle')}
                    message1={t('login.noPermissionMessage1')}
                    message2={t('login.noPermissionMessage2')}
                    onRetry={() => navigate({ to: '/login' })}
                    retryButtonLabel={t('login.backButton')}
                />
            </AuthLayout>
        )
    }

    // Show expired invitation message if fetch fails
    if (invitationError) {
        return (
            <AuthLayout maxWidth="sm">
                <AuthExpiredState
                    title={t('setPassword.expired.title', 'Lời mời hết hạn')}
                    description={t('setPassword.expired.description', 'Lời mời tham gia hệ thống đã hết hạn.')}
                    message={t(
                        'setPassword.expired.submitMessage',
                        'Vui lòng liên hệ Quản trị viên hoặc bộ phận liên quan để được hỗ trợ.',
                    )}
                    onRetry={() => navigate({ to: '/login' })}
                    retryButtonLabel={t('setPassword.backToLogin', 'Về đăng nhập')}
                />
            </AuthLayout>
        )
    }

    return (
        <AuthLayout maxWidth="lg">
            <div className="text-center mb-6">
                <h1 className="text-[24px] font-bold mb-2">{t('setPassword.title')}</h1>
                <p className="text-sm text-muted-foreground">{t('setPassword.description')}</p>
            </div>

            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        {/* Left Column */}
                        <div className="space-y-4">
                            <FormField
                                control={form.control}
                                name="fullName"
                                render={({ field }) => (
                                    <FormItem className="relative">
                                        <FormLabel required>{t('setPassword.labels.fullName')}</FormLabel>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                disabled={true}
                                                className="h-10 text-sm bg-muted rounded-lg border-border"
                                                placeholder={t('setPassword.placeholders.fullName')}
                                            />
                                        </FormControl>
                                        <FormMessage className="absolute left-0 top-[calc(100%-20px)] pt-2" />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="regionId"
                                render={({ field }) => (
                                    <FormItem className="relative">
                                        <FormLabel required>{t('setPassword.labels.region')}</FormLabel>
                                        <FormControl>
                                            <div className="h-10 flex items-center text-sm bg-muted rounded-lg border border-border px-3">
                                                {invitationInfo?.region?.name ? (
                                                    <span className="text-foreground">
                                                        {invitationInfo.region.name}
                                                    </span>
                                                ) : (
                                                    <span className="text-muted-foreground">
                                                        {t('setPassword.placeholders.region')}
                                                    </span>
                                                )}
                                            </div>
                                        </FormControl>
                                        <FormMessage className="absolute left-0 top-[calc(100%-20px)] pt-2" />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="password"
                                render={({ field }) => (
                                    <FormItem className="relative pb-5">
                                        <FormLabel required>{t('setPassword.labels.password')}</FormLabel>
                                        <FormControl>
                                            <PasswordInput
                                                placeholder={t('setPassword.placeholders.password')}
                                                className="h-10 text-sm bg-background rounded-lg border-border"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage className="absolute left-0 top-[calc(100%-20px)] pt-2" />
                                    </FormItem>
                                )}
                            />
                        </div>

                        {/* Right Column */}
                        <div className="space-y-4">
                            <FormField
                                control={form.control}
                                name="position"
                                render={({ field }) => (
                                    <FormItem className="relative ">
                                        <FormLabel>{t('setPassword.labels.position')}</FormLabel>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                disabled={true}
                                                className="h-10 text-sm bg-muted rounded-lg border-border"
                                                placeholder={t('setPassword.placeholders.position')}
                                            />
                                        </FormControl>
                                        <FormMessage className="absolute left-0 top-[calc(100%-20px)] pt-2" />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="areaIds"
                                render={({ field }) => {
                                    const areaNames = invitationInfo?.areas?.map(a => a.name).filter(Boolean) || []
                                    const areaJoined = areaNames.join(', ')
                                    const display =
                                        areaNames.length > 2
                                            ? `${areaNames.slice(0, 2).join(', ')}...`
                                            : areaJoined || ''

                                    return (
                                        <FormItem className="relative ">
                                            <FormLabel required>{t('setPassword.labels.area')}</FormLabel>
                                            <FormControl>
                                                <div
                                                    className="h-10 flex items-center text-sm bg-muted rounded-lg border border-border px-3"
                                                    title={areaNames.length > 0 ? areaJoined : undefined}
                                                >
                                                    {areaNames.length > 0 ? (
                                                        <span className="text-foreground truncate">{display}</span>
                                                    ) : (
                                                        <span className="text-muted-foreground">
                                                            {t('setPassword.placeholders.area')}
                                                        </span>
                                                    )}
                                                </div>
                                            </FormControl>
                                            <FormMessage className="absolute left-0 top-[calc(100%-20px)] pt-2" />
                                        </FormItem>
                                    )
                                }}
                            />

                            <FormField
                                control={form.control}
                                name="confirmPassword"
                                render={({ field }) => (
                                    <FormItem className="relative pb-5">
                                        <FormLabel required>{t('setPassword.labels.confirmPassword')}</FormLabel>
                                        <FormControl>
                                            <PasswordInput
                                                placeholder={t('setPassword.placeholders.confirmPassword')}
                                                className="h-10 text-sm bg-background rounded-lg border-border"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage className="absolute left-0 top-[calc(100%-20px)] pt-2" />
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>

                    <div className="flex justify-center mt-6">
                        <Button
                            variant="default"
                            type="submit"
                            className="w-sm rounded-lg text-text-accent"
                            isDisabled={isLoading}
                        >
                            {isLoading ? t('setPassword.submitting') : t('setPassword.submitButton')}
                        </Button>
                    </div>
                </form>
            </Form>
        </AuthLayout>
    )
}
